export default function AboutMission() {
  return (
    <section className="py-28 bg-[#0e0e0e] relative overflow-hidden">
      {/* Subtle ambient glow background */}
      <div className="absolute top-0 left-0 w-1/2 h-full bg-[radial-gradient(ellipse_at_top_left,rgba(147,210,250,0.06)_0%,transparent_60%)] pointer-events-none" />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(ellipse_at_top_right,rgba(251,146,60,0.07)_0%,transparent_60%)] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">

        {/* Label */}
        <div className="flex items-center justify-center gap-3 mb-16">
          <span className="h-px w-12 bg-[#93d2fa]/40" />
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-stone-400">Our Story</span>
          <span className="h-px w-12 bg-orange-500/40" />
        </div>

        {/* Opening */}
        <p className="text-center text-stone-400 text-base leading-relaxed mb-4 italic">
          In the beginning, there was only silence…
        </p>
        <p className="text-center text-stone-500 text-sm leading-relaxed mb-16 italic">
          and the struggle to be seen within it.
        </p>

        {/* Ice paragraph */}
        <div className="mb-10 border-l-2 border-[#93d2fa]/60 pl-6">
          <p className="text-stone-300 text-base leading-loose">
            Then came the{' '}
            <span className="text-[#93d2fa] font-semibold">cold</span>
            —where ideas froze, unseen and unheard. Campaigns faded into darkness, lost in a world that never noticed they existed.
          </p>
        </div>

        {/* Fire intro */}
        <div className="mb-16 border-l-2 border-orange-500/70 pl-6">
          <p className="text-stone-300 text-base leading-loose">
            And then… came the{' '}
            <span className="text-orange-400 font-semibold">fire</span>.
          </p>
        </div>

        {/* Dual image split */}
        <div className="grid grid-cols-2 gap-3 mb-16 rounded-2xl overflow-hidden h-56 md:h-72">
          <div className="relative overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=ethereal%20frozen%20ice%20landscape%20at%20night%20with%20deep%20blue%20glaciers%20and%20cold%20mist%2C%20no%20people%2C%20long%20exposure%20photography%2C%20blue%20and%20white%20tones%2C%20dark%20sky%2C%20hauntingly%20beautiful%20arctic%20scene%2C%20cinematic%20wide%20shot&width=600&height=400&seq=mission-ice-01&orientation=landscape"
              alt="The cold — precision and strategy"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0e0e0e]/80" />
            <div className="absolute bottom-4 left-4">
              <span className="text-[#93d2fa] text-xs font-bold uppercase tracking-widest">The Cold</span>
            </div>
          </div>
          <div className="relative overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=dramatic%20close%20up%20of%20molten%20fire%20and%20glowing%20embers%20exploding%20outward%20on%20a%20black%20background%2C%20deep%20orange%20and%20red%20fire%2C%20long%20exposure%20photography%2C%20no%20people%2C%20cinematic%20abstract%20fire%20texture%2C%20powerful%20and%20intense&width=600&height=400&seq=mission-fire-01&orientation=landscape"
              alt="The fire — power and creation"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0e0e0e]/80" />
            <div className="absolute bottom-4 right-4">
              <span className="text-orange-400 text-xs font-bold uppercase tracking-widest">The Fire</span>
            </div>
          </div>
        </div>

        {/* Core origin */}
        <div className="text-center mb-16">
          <p className="text-stone-200 text-lg md:text-xl leading-relaxed font-light mb-6">
            AdForge AI was born between these two forces —
            <span className="text-[#93d2fa] font-semibold"> ice</span> and
            <span className="text-orange-400 font-semibold"> fire</span>.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center mt-8">
            <div className="flex-1 max-w-xs mx-auto text-left border border-[#93d2fa]/20 rounded-xl p-5 bg-[#93d2fa]/5">
              <div className="text-[#93d2fa] text-xs font-bold uppercase tracking-widest mb-3">From the Cold</div>
              <p className="text-stone-300 text-sm leading-relaxed">We learned <strong className="text-[#93d2fa]">precision</strong>. Clarity. Strategy.</p>
            </div>
            <div className="flex-1 max-w-xs mx-auto text-left border border-orange-500/20 rounded-xl p-5 bg-orange-500/5">
              <div className="text-orange-400 text-xs font-bold uppercase tracking-widest mb-3">From the Fire</div>
              <p className="text-stone-300 text-sm leading-relaxed">We forged <strong className="text-orange-400">power</strong>. Speed. Creation without hesitation.</p>
            </div>
          </div>
        </div>

        {/* The contrast lines */}
        <div className="grid sm:grid-cols-2 gap-4 mb-16">
          <div className="rounded-xl border border-stone-800 px-6 py-4 bg-stone-900/40">
            <p className="text-stone-400 text-sm leading-relaxed">
              Where others are paralyzed by doubt…{' '}
              <span className="text-orange-400 font-semibold">we ignite.</span>
            </p>
          </div>
          <div className="rounded-xl border border-stone-800 px-6 py-4 bg-stone-900/40">
            <p className="text-stone-400 text-sm leading-relaxed">
              Where others disappear into the frost…{' '}
              <span className="text-[#93d2fa] font-semibold">we endure.</span>
            </p>
          </div>
        </div>

        {/* Declaration */}
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <p className="text-stone-500 text-sm mb-2 italic">This is not creation by chance.</p>
          <p className="text-stone-500 text-sm mb-8 italic">This is not guesswork whispered into the dark.</p>
          <p className="text-stone-200 text-lg leading-relaxed font-light">
            This is the balance of opposites —
          </p>
          <p className="text-[#93d2fa] font-semibold text-base mt-2">calculated like ice…</p>
          <p className="text-orange-400 font-semibold text-base mt-1">unleashed like fire.</p>
        </div>

        {/* Forging line */}
        <div className="border-t border-stone-800 pt-12 text-center mb-12">
          <p className="text-stone-300 text-base leading-loose max-w-2xl mx-auto">
            Here, every idea is <span className="text-[#93d2fa]">sharpened in the cold</span>,<br />
            then thrust into the flames until it emerges{' '}
            <span className="text-orange-400 font-semibold">stronger, louder… impossible to ignore.</span>
          </p>
        </div>

        {/* The closing — winter is coming */}
        <div className="rounded-2xl border border-stone-700/50 bg-stone-900/60 px-8 py-10 text-center max-w-2xl mx-auto">
          <p className="text-stone-400 text-sm leading-relaxed mb-4">
            So whether you find yourself in the long night —<br />
            or standing on the edge of conquest —
          </p>
          <p className="text-stone-300 text-base font-light mb-6">Know this:</p>
          <p className="text-[#93d2fa] text-lg font-semibold mb-2">Winter may come…</p>
          <p className="text-orange-400 text-xl font-black tracking-wide">but fire decides who survives.</p>
        </div>

      </div>
    </section>
  );
}
